import React, { useContext, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, LockIcon, SmartphoneIcon, ThermometerIcon } from 'lucide-react';
import { WallCard } from './WallCard';
import { DoorDetection } from './DoorDetection';
import { SlideToConfirm } from '../../ui/SlideToConfirm';
import { CardPingContext } from '../../../contexts/CardPingContext';
import { useCountdown } from '../../../hooks/useCountdown';
import { useDb } from '../../../hooks/useDb';
import { usePingOn } from '../../../hooks/usePingOn';
import { db } from '../../../utils/db';
import { deviceById, roomById } from '../../../utils/dbSelectors';
import type { RefusalSubject } from '../../../types/stage';

/** Nooit automatisch: the door is shown, never opened — no button, only where to do it yourself. */
export function DeurCard() {
  const cam = useDb(t => deviceById(t, 'cam-voordeur'));
  const ping = useContext(CardPingContext);
  if (!cam?.image || !cam.detection) return null;
  return <WallCard agent="huis" kicker="voordeur" title={cam.status} level="nooit" where="lokaal" traceId="deur" data-id="element-1285" data-mp="1cyksq7rqgq7r">
      <DoorDetection image={cam.image} maskHeight={cam.maskHeight ?? 0.2} detection={cam.detection} onDetected={ping} data-id="element-1286" data-mp="17yonk57ausqm" />
      <p className="mt-3 text-ink-muted" data-id="element-1287" data-mp="12yk4q8ix5lx8">visiemodel · lokaal · {cam.privacyMask} gemaskeerd</p>
      <p className="mt-2 flex items-center gap-3 text-ink" data-id="element-1288" data-mp="yx8fi43yj2pk">
        <LockIcon className="h-6 w-6 shrink-0 text-ink-muted" aria-hidden data-id="element-1289" data-mp="tyx2jkid8c34" />
        Open zelf via de deurbel-app
      </p>
    </WallCard>;
}
const RING_R = 64;
const RING_C = 2 * Math.PI * RING_R;

/** Niveau 2: announced, runs after a visible 60 s countdown unless you tap Stop. */
export function VerwarmingCard({
  onDone,
  onSound
}: {
  onDone: () => void;
  onSound: () => void;
}) {
  const {
    remaining,
    status,
    stop
  } = useCountdown(60000, true);
  const room = useDb(t => roomById(t, 'werkkamer'));
  const seconds = Math.ceil(remaining / 1000);
  usePingOn(Math.ceil(seconds / 10));
  usePingOn(status);
  useEffect(() => {
    if (status === 'klaar') {
      db.setTarget('werkkamer', 17, 'tenzijBezwaar');
      onSound();
      const id = window.setTimeout(onDone, 2600);
      return () => window.clearTimeout(id);
    }
    if (status === 'gestopt') {
      db.log({
        agent: 'huis',
        summary: 'Verwarming werkkamer: gestopt door jou',
        level: 'tenzijBezwaar',
        where: 'lokaal',
        outcome: 'gestopt',
        traceId: 'verwarming'
      });
      const id = window.setTimeout(onDone, 3200);
      return () => window.clearTimeout(id);
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = status === 'klaar';
  const stopped = status === 'gestopt';
  return <WallCard agent="huis" kicker={done ? 'gedaan' : stopped ? 'gestopt' : 'tenzij je stopt'} title={done ? 'Gedaan' : stopped ? 'Gestopt' : 'Verwarming werkkamer naar 17 °C terwijl je weg bent'} level="tenzijBezwaar" where="lokaal" traceId="verwarming" data-id="element-1290" data-mp="5ku7tn373l9e">
      {done || stopped ? <p className="flex items-center gap-4 py-2 text-ink" data-id="element-1291" data-mp="2nry1ylw5fd43">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-accent/15 text-accent" data-id="element-1292" data-mp="2nq4vp7pg6uwm">
            {done ? <CheckIcon className="h-6 w-6" aria-hidden data-id="element-1293" data-mp="2wkjl433ozltg" /> : <ThermometerIcon className="h-6 w-6" aria-hidden data-id="element-1294" data-mp="2wjhzx5sypiy0" />}
          </span>
          {done ? `De werkkamer staat op ${room?.target ?? 17} °C.` : `Niets veranderd. Blijft op ${room?.target ?? 20} °C.`}
        </p> : <div className="flex flex-wrap items-center gap-5" data-id="element-1295" data-mp="24vpynnmp5cbf">
          <div className="relative h-[140px] w-[140px] shrink-0" role="timer" aria-label={`Nog ${seconds} seconden`} data-id="element-1296" data-mp="1zvu38cs2et7h">
            <svg viewBox="0 0 150 150" className="h-full w-full -rotate-90" aria-hidden data-id="element-1297" data-mp="2dvlc5z3q5jem">
              <circle cx="75" cy="75" r={RING_R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" data-id="element-1298" data-mp="28tnlijszvmbn" />
              <circle cx="75" cy="75" r={RING_R} fill="none" stroke="rgb(var(--accent))" strokeWidth="7" strokeLinecap="round" strokeDasharray={RING_C} strokeDashoffset={RING_C * (1 - remaining / 60000)} data-id="element-1299" data-mp="3p7m5lrbj9w0p" />
            </svg>
            <span className="absolute inset-0 grid place-items-center font-display text-[48px] font-light tabular-nums text-ink" data-id="element-1300" data-mp="tcj7uxiz2u78">{seconds}</span>
          </div>
          <button type="button" onClick={stop} className="h-20 min-w-[140px] flex-1 whitespace-nowrap rounded-2xl bg-ink text-[30px] font-semibold text-night-950 transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-1301" data-mp="12ea8vp2kicur">
            Stop
          </button>
        </div>}
    </WallCard>;
}
export function LichtCard({
  room = 'werkkamer'
}: {
  room?: string;
}) {
  const r = useDb(t => roomById(t, room), [room]);
  const value = r?.light ?? 0;
  usePingOn(value);
  return <WallCard agent="huis" kicker={r?.name.toLowerCase() ?? room} title={value === 0 ? 'Licht uit' : `Licht op ${value}%`} level="zelf" where="lokaal" traceId="licht" data-id="element-1302" data-mp="3aldu7vva36zi">
      <div className="h-3 overflow-hidden rounded-full bg-white/[0.08]" data-id="element-1303" data-mp="36m4zoosez52w">
        <motion.div className="h-full rounded-full bg-[#F4E4B8]" initial={{
        width: '0%'
      }} animate={{
        width: `${value}%`
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1304" data-mp="31lqrn6qtbv4v" />
      </div>
    </WallCard>;
}
export function KlimaatCard({
  room = 'werkkamer'
}: {
  room?: string;
}) {
  const r = useDb(t => roomById(t, room), [room]);
  usePingOn(r?.target);
  if (!r) return null;
  return <WallCard agent="huis" kicker={r.name.toLowerCase()} title={`Naar ${r.target} °C`} level="zelf" where="lokaal" traceId="klimaat" data-id="element-1305" data-mp="3fkqec8urlzye">
      <p className="flex items-center gap-3" data-id="element-1306" data-mp="gvrwxyys3ik7">
        <ThermometerIcon className="h-6 w-6 shrink-0 text-ink-muted" aria-hidden data-id="element-1307" data-mp="gwji0fur1xw8" /> Nu {String(r.temp).replace('.', ',')} °C
      </p>
    </WallCard>;
}
const REFUSAL_COPY: Record<RefusalSubject, {
  title: string;
  body: string;
}> = {
  voordeur: {
    title: 'Voordeur blijft dicht',
    body: 'Sloten bedien je zelf, via de deurbel-app.'
  },
  alarm: {
    title: 'Alarm blijft zoals het is',
    body: 'Het alarm bedien je zelf, via het paneel bij de voordeur.'
  },
  betaling: {
    title: 'Geen betaling gedaan',
    body: 'Betalingen doe je zelf in je bank-app.'
  },
  verwijderen: {
    title: 'Niets verwijderd',
    body: 'Verwijderen doe je zelf.'
  }
};

/** Nooit automatisch: information only — never a button, never a countdown. */
export function GeweigerdCard({
  subject = 'voordeur'
}: {
  subject?: RefusalSubject;
}) {
  const copy = REFUSAL_COPY[subject];
  return <WallCard agent="huis" kicker="geweigerd" title={copy.title} level="nooit" where="lokaal" traceId={`weigering-${subject}`} data-id="element-1308" data-mp="1ilht2qr99xq0">
      <p className="flex items-start gap-3" data-id="element-1309" data-mp="381r00hm5uylw">
        <LockIcon className="mt-0.5 h-6 w-6 shrink-0 text-ink-muted" aria-hidden data-id="element-1310" data-mp="3d00roi8ofh2r" />
        <span data-id="element-1311" data-mp="3cxy2g0fclrz3">
          {copy.body} <span className="text-ink-muted" data-id="element-1312" data-mp="3n3pk4o2pq6rd">Vastgelegd in het logboek.</span>
        </span>
      </p>
    </WallCard>;
}
export function MailDraftCard({
  onApproved
}: {
  onApproved: () => void;
}) {
  const draft = useDb(t => t.mailDrafts.find(d => d.id === 'd-bakker-voice'));
  if (!draft) return null;
  return <WallCard agent="mail" kicker="concept" title="Aan klant Bakker" level="naOk" where={draft.where} traceId="mail-voice" data-id="element-1313" data-mp="3r4rmvevu90y3">
      <p className="text-ink" data-id="element-1314" data-mp="9nhxiwqcbvex">“{draft.body}”</p>
      <div className="mt-4" data-id="element-1315" data-mp="4qrgo76povsg">
        <SlideToConfirm size="wall" label="Schuif om te versturen" confirmedLabel="Verstuurd" onConfirm={() => {
        db.log({
          agent: 'mail',
          summary: 'Mail aan klant Bakker verstuurd',
          level: 'naOk',
          where: draft.where,
          outcome: 'goedgekeurd',
          traceId: 'mail-voice'
        });
        onApproved();
      }} data-id="element-1316" data-mp="4pgcw7d9no1c" />
      </div>
      <p className="mt-3 flex items-center gap-2.5 text-ink-muted" data-id="element-1317" data-mp="2ggkdv2jg0izl">
        <SmartphoneIcon className="h-6 w-6 shrink-0" aria-hidden data-id="element-1318" data-mp="350qotxcvwxaa" /> Of bevestig op je telefoon
      </p>
    </WallCard>;
}